import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { ContentDetailSection, DetailEditButton } from '~src/app/content/contentDetailDialog/ContentDetailSection';
import { MapDispatchToProps } from '~src/app/utils/ConnectUtils';
import { Messages } from '~src/codegen/Localize';
import { updateDataFreshnessPolicy } from '~src/dataAcceleration/actions/UpdateDataFreshnessPolicyAction';
import { cssClass } from '~src/style';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { CachingPolicyType, IWorkbookDataFreshnessDescription } from '~src/ts/models/IDataFreshnessInfo';
import { IWorkbookDetailInfoWithUsage } from '~src/workbook/models/IWorkbookInfo';

import { dataFreshnessPolicyTimeZone, findDataFreshnessSummary } from '../utils/DataFreshnessUtils';

interface DataFreshnessPolicyDetailSectionOwnProps {
  readonly workbook: IWorkbookDetailInfoWithUsage;
  readonly editDataFreshnessAllowed: boolean;
}

interface DataFreshnessPolicyContentDispatchProps {
  readonly editDataFreshnessPolicy: (items: IWorkbookDetailInfoWithUsage[]) => void;
}

export type DataFreshnessPolicyDetailSectionProps = DataFreshnessPolicyDetailSectionOwnProps & DataFreshnessPolicyContentDispatchProps;

export function DataFreshnessPolicyDetailSection(props: DataFreshnessPolicyDetailSectionProps): JSX.Element {
  const shouldShowEditButton = props.editDataFreshnessAllowed;
  return (
    <ContentDetailSection
      header={Messages.dataFreshness()}
      detail={<DataFreshnessPolicyDescription workbook={props.workbook} />}
      // ContentDetailSection will compose the actual testId to be 'content-detail-section-data-freshness-detail'
      testId={'data-freshness'}
      isActionAllowed={shouldShowEditButton}
      action={shouldShowEditButton ? <DetailEditButton
          buttonLabel={Messages.editDataFreshnessPolicyButtonText()}
          buttonTestId={'edit-data-freshness-policy'}
          onClickAction={() => props.editDataFreshnessPolicy([props.workbook])}
        /> : null
      }
    />
  );
}

function DataFreshnessPolicyDescription(props: { workbook: IWorkbookDetailInfoWithUsage }): JSX.Element {
  return (<>
    <p className={descriptionText}>
        {findDataFreshnessSummary(props.workbook.workbookDataFreshness)}
    </p>
    {needsShowTimeZone(props.workbook.workbookDataFreshness) && <p className={descriptionText}>
      {dataFreshnessPolicyTimeZone(props.workbook.workbookDataFreshness)}
    </p>}
  </>);
}

function needsShowTimeZone(workbookDataFreshness: IWorkbookDataFreshnessDescription) {
  return workbookDataFreshness !== undefined
    && (workbookDataFreshness.cachingPolicyType === CachingPolicyType.REPEAT_DAILY ||
      workbookDataFreshness.cachingPolicyType === CachingPolicyType.REPEAT_WEEKLY ||
      workbookDataFreshness.cachingPolicyType === CachingPolicyType.REPEAT_MONTHLY)
    && workbookDataFreshness.timezone !== undefined;
}

const descriptionText = cssClass('content-detail-section','description', {
  marginBlockStart: '0px',
  marginBlockEnd: '0px'
});

const mapDispatchToProps: MapDispatchToProps<DataFreshnessPolicyContentDispatchProps> = (dispatch) => bindActionCreators({
  editDataFreshnessPolicy: updateDataFreshnessPolicy,
}, dispatch);

export const ConnectedDataFreshnessPolicyDetailSection: React.ComponentClass<DataFreshnessPolicyDetailSectionOwnProps> =
  connect(undefined, mapDispatchToProps)(DataFreshnessPolicyDetailSection);

export function shouldShowDataFreshnessDialog(workbook: IWorkbookDetailInfoWithUsage) {
  // it means data freshness does not apply if defaultCacheTimeoutInMinutes is undefined
  // this will happen when the workbook does not use any live datasource
  return FeatureFlags.queryPipelineDataFreshnessUISupported && workbook.workbookDataFreshness !== undefined;
}

